import React, { useCallback, useEffect, useState, lazy } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, TrendingUp, Monitor, Calendar, Headset, BarChart3, Target, Users, ShieldCheck, Zap, LayoutTemplate, Palette, MessageSquareQuote, ExternalLink, Shield } from 'lucide-react';
// Brand Colors
const colors = {
  teal: '#2EAD6D',
  amber: '#F5A623',
  dark: '#0F0F0F',
  darkCard: '#1A1A1A'
};
const LOGO_LEVATE = 'https://cdn.magicpatterns.com/uploads/txUGJVNRA288c6kQWwcnmn/logo.png';
const LOGO_VIRIDIA = 'https://cdn.magicpatterns.com/uploads/wR1kBZZTaK1wFgutmfc7P9/pasted-image.png';
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 15;
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  // Slide Renderers
  const slides = [
  // SLIDE 1: TITLE
  () => <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-1">
        <div className="flex items-center justify-center gap-8 mb-12" data-id="element-2">
          <div className="bg-white/5 p-6 rounded-2xl backdrop-blur-sm border border-white/10" data-id="element-3">
            <img src={LOGO_LEVATE} alt="L-Evate" className="h-16 md:h-20 object-contain" data-id="element-4" />
          </div>
          <span className="text-3xl font-light text-gray-500" data-id="element-5">x</span>
          <div className="rounded-2xl" data-id="element-6">
            <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-32 md:h-40 object-contain" data-id="element-7" />
          </div>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-8">
          L-EVATE NETWORK <span className="text-[#2EAD6D]" data-id="element-9">x</span> VIRIDIA
          ANALYTICS
        </h1>
        <h2 className="text-2xl md:text-3xl text-[#F5A623] font-medium mb-4" data-id="element-10">
          Licensee Partnership & Onboarding Program
        </h2>
        <p className="text-xl text-gray-400 max-w-2xl" data-id="element-11">
          A Comprehensive Guide to Growth & Success
        </p>
        <div className="mt-16 w-24 h-1 bg-gradient-to-r from-[#F5A623] to-[#2EAD6D] rounded-full" data-id="element-12"></div>
      </div>,
  // SLIDE 2: AGENDA
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-13">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-14">
          Agenda
        </h2>
        <p className="text-xl text-[#2EAD6D] mb-12" data-id="element-15">
          We'll cover what this partnership means for you
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16">
          {['Pricing & Fees', 'What you get as a partner', 'Brand | Templates | Customization', 'Support', 'Ongoing Performance Optimization', 'Your responsibilities'].map((item, idx) => <div key={idx} className="flex items-center gap-4 bg-[#1A1A1A] p-6 rounded-xl border border-white/5 hover:border-[#F5A623]/30 transition-colors" data-id="element-17">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2EAD6D]/10 text-[#2EAD6D] font-bold text-lg border border-[#2EAD6D]/20" data-id="element-18">
                {idx + 1}
              </div>
              <span className="text-lg text-gray-200 font-medium" data-id="element-19">{item}</span>
            </div>)}
        </div>
      </div>,
  // SLIDE 3: OVERVIEW
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-20">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-21">
          Strategic Partnership Overview
        </h2>
        <p className="text-xl text-[#F5A623] mb-12" data-id="element-22">
          What You Get as a Licensee Partner
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-23">
          {[{
        icon: Target,
        text: 'Core Meta (FB/IG) Ad Management - Optimized for Lead Gen'
      }, {
        icon: Palette,
        text: 'Custom Ad Creative Development (Up to 3 per month)'
      }, {
        icon: LayoutTemplate,
        text: '3-Page High-Converting Website (Hosted & Managed)'
      }, {
        icon: Calendar,
        text: 'Integrated Telehealth Booking System'
      }, {
        icon: Headset,
        text: 'Dedicated Support Ticket System (24-hr typical turnaround)'
      }, {
        icon: TrendingUp,
        text: 'Monthly 1-on-1 Strategy Optimization Calls (30 mins)'
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 relative overflow-hidden group" data-id="element-24">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#2EAD6D] to-[#F5A623] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-25"></div>
              <item.icon className="w-10 h-10 text-[#2EAD6D] mb-6" data-id="element-26" />
              <p className="text-lg text-gray-200 leading-relaxed" data-id="element-27">
                {item.text}
              </p>
            </div>)}
        </div>
      </div>,
  // SLIDE 4: PRICING
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-28">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center" data-id="element-29">
          Transparent Partner Pricing
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-30">
          <div className="bg-[#1A1A1A] rounded-3xl p-10 border border-white/10 relative overflow-hidden" data-id="element-31">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#2EAD6D]/10 rounded-full blur-3xl" data-id="element-32"></div>
            <h3 className="text-2xl text-gray-400 font-medium mb-4" data-id="element-33">
              Ad Management
            </h3>
            <div className="text-6xl font-bold text-white mb-6" data-id="element-34">
              10%{' '}
              <span className="text-2xl text-gray-500 font-normal" data-id="element-35">
                of Spend
              </span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-36">
              <li className="flex items-center gap-3 text-gray-300" data-id="element-37">
                <CheckCircle2 className="w-5 h-5 text-[#2EAD6D]" data-id="element-38" /> Ongoing
                optimization & reporting
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-39">
                <CheckCircle2 className="w-5 h-5 text-[#2EAD6D]" data-id="element-40" /> Billed
                monthly based on spend
              </li>
            </ul>
            <p className="text-sm text-gray-500 mt-auto" data-id="element-41">
              * Ad spend is paid directly to platforms (Meta/Google)
            </p>
          </div>

          <div className="bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] rounded-3xl p-10 border border-[#F5A623]/30 relative overflow-hidden shadow-[0_0_40px_rgba(245,166,35,0.1)]" data-id="element-42">
            <div className="absolute top-0 right-0 px-4 py-1 bg-[#F5A623] text-black text-sm font-bold rounded-bl-lg" data-id="element-43">
              POPULAR
            </div>
            <h3 className="text-2xl text-gray-400 font-medium mb-4" data-id="element-44">
              Monthly Retainer
            </h3>
            <div className="text-6xl font-bold text-white mb-2" data-id="element-45">
              $325{' '}
              <span className="text-2xl text-gray-500 font-normal" data-id="element-46">/mo</span>
            </div>
            <p className="text-[#F5A623] font-medium mb-6 line-through decoration-red-500/50" data-id="element-47">
              Standard $497 rate
            </p>
            <ul className="space-y-4" data-id="element-48">
              <li className="flex items-center gap-3 text-gray-300" data-id="element-49">
                <CheckCircle2 className="w-5 h-5 text-[#F5A623]" data-id="element-50" /> Includes Ads
                Management
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-51">
                <CheckCircle2 className="w-5 h-5 text-[#F5A623]" data-id="element-52" /> Website
                Hosting & Maintenance
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-53">
                <CheckCircle2 className="w-5 h-5 text-[#F5A623]" data-id="element-54" /> Booking
                System Integration
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#2EAD6D]/20 to-[#F5A623]/20 border border-[#2EAD6D]/30 rounded-2xl p-6 text-center" data-id="element-55">
          <h4 className="text-xl font-bold text-white mb-2 flex items-center justify-center gap-2" data-id="element-56">
            <Zap className="w-6 h-6 text-[#F5A623]" data-id="element-57" /> Launch Bundle
          </h4>
          <p className="text-gray-300" data-id="element-58">
            Get a <strong className="text-[#2EAD6D]" data-id="element-59">10% Discount</strong> on
            any add-ons purchased within the first 14 days of launch.
          </p>
        </div>
      </div>,
  // SLIDE 5: ADD-ONS
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-60">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-61">
          A La Carte Add-Ons
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#1A1A1A]" data-id="element-62">
          <table className="w-full text-left border-collapse" data-id="element-63">
            <thead data-id="element-64">
              <tr className="border-b border-white/10 bg-black/40" data-id="element-65">
                <th className="p-6 text-gray-400 font-medium" data-id="element-66">Service</th>
                <th className="p-6 text-gray-400 font-medium" data-id="element-67">Regular Price</th>
                <th className="p-6 text-[#F5A623] font-bold bg-[#F5A623]/5" data-id="element-68">
                  14-Day Price (10% Off)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-69">
              {[['GHL Phone Number', '$10/mo', '$9/mo'], ['Email Marketing Campaigns', '$197 per campaign', '$177.30 per campaign'], ['Social Media Automation', '$247/mo', '$222.30/mo'], ['SMS Broadcast Messaging', '$97/mo', '$87.30/mo'], ['Advanced Pipeline Customization', '$497 one-time', '$447.30 one-time'], ['AI Voice Agent', '$297/mo', '$267.30/mo']].map((row, idx) => <tr key={idx} className="hover:bg-white/5 transition-colors" data-id="element-70">
                  <td className="p-6 text-gray-200 font-medium" data-id="element-71">{row[0]}</td>
                  <td className="p-6 text-gray-400" data-id="element-72">{row[1]}</td>
                  <td className="p-6 text-[#2EAD6D] font-bold bg-[#F5A623]/5" data-id="element-73">
                    {row[2]}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>,
  // SLIDE 6: BILLING
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-74">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-10" data-id="element-75">
          How Billing Works
        </h2>

        <div className="space-y-6" data-id="element-76">
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-[#2EAD6D]" data-id="element-77">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-78">
              <span className="bg-[#2EAD6D]/20 text-[#2EAD6D] p-2 rounded-lg" data-id="element-79">
                <Target className="w-6 h-6" data-id="element-80" />
              </span>
              Ad Spend
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-81">
              Your payment method will be added on file to your{' '}
              <strong data-id="element-82">OWN Meta Ads Account</strong>. You will grant Viridia
              Analytics Partner permissions to create, manage, and track your
              campaigns. We will accomplish this during initial onboarding.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-[#F5A623]" data-id="element-83">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-84">
              <span className="bg-[#F5A623]/20 text-[#F5A623] p-2 rounded-lg" data-id="element-85">
                <ShieldCheck className="w-6 h-6" data-id="element-86" />
              </span>
              Meta Ad Account Threshold
            </h3>
            <p className="text-gray-300 leading-relaxed" data-id="element-87">
              New Meta Ad Accounts may be subject to a limited billing threshold
              as the account builds credibility over time. Until granted access
              from Meta, we cannot control your billing threshold.
              <strong className="text-white block mt-2" data-id="element-88">
                We CAN control Campaign Budget, Creatives, Copies, and Ad Goals.
              </strong>
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border-l-4 border-gray-500" data-id="element-89">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3" data-id="element-90">
              <span className="bg-gray-500/20 text-gray-400 p-2 rounded-lg" data-id="element-91">
                <Calendar className="w-6 h-6" data-id="element-92" />
              </span>
              Invoicing
            </h3>
            <p className="text-gray-300 leading-relaxed mb-4" data-id="element-93">
              Viridia Analytics invoices separately for the monthly retainer and
              10% management fee, based on your previous month's ad spend.
            </p>
            <div className="text-sm text-gray-400 bg-black/30 p-4 rounded-lg" data-id="element-94">
              <p className="mb-2" data-id="element-95">
                • The 10% ad management fee will also include advertising on
                other platforms should the licensee opt-in to multi-channel
                advertising.
              </p>
              <p data-id="element-96">
                • Licensee is required to sign a service agreement for other
                products, and ad campaign platforms.
              </p>
            </div>
          </div>
        </div>
      </div>,
  // SLIDE 7: ROADMAP
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-97">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-98">
          Onboarding Roadmap
        </h2>
        <p className="text-xl text-[#2EAD6D] mb-16" data-id="element-99">
          Phase 1: The First 30 Days
        </p>

        <div className="relative" data-id="element-100">
          {/* Connecting Line */}
          <div className="hidden md:block absolute top-8 left-0 w-full h-1 bg-gradient-to-r from-[#2EAD6D] via-[#F5A623] to-[#2EAD6D] opacity-30" data-id="element-101"></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-102">
            {[{
          week: 'Week 1',
          title: 'Branding & Intake',
          items: ['Discovery & Brand Intake', 'Logo Design/Selection', 'Template Selection', 'Secure Domain', 'Integrations']
        }, {
          week: 'Week 2',
          title: 'Website & SEO',
          items: ['Refinements', 'Website Build & SEO', 'Change Orders & Peak Review', 'Creatives Started']
        }, {
          week: 'Week 3',
          title: 'Review & Hosting',
          items: ['Final Approved', 'Final Approvals', 'Hosting Setup', 'Approvals']
        }, {
          week: 'Week 4',
          title: 'Go-Live',
          items: ['Site Live!', 'Established', 'Campaign Launch', '(Pending Legit Script Certification)']
        }].map((col, idx) => <div key={idx} className="relative pt-8 md:pt-16" data-id="element-103">
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute top-6 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#0F0F0F] border-4 border-[#2EAD6D] z-10" data-id="element-104"></div>

                <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-white/5 h-full hover:border-[#2EAD6D]/50 transition-colors" data-id="element-105">
                  <div className="text-[#F5A623] font-bold text-sm tracking-wider uppercase mb-2" data-id="element-106">
                    {col.week}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-white/10" data-id="element-107">
                    {col.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-108">
                    {col.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-300 text-sm" data-id="element-109">
                        <CheckCircle2 className="w-4 h-4 text-[#2EAD6D] mt-0.5 shrink-0" data-id="element-110" />
                        <span data-id="element-111">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </div>,
  // SLIDE 8: REVIEWS
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-112">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-113">
          Performance Reviews
        </h2>
        <p className="text-xl text-[#F5A623] mb-12" data-id="element-114">Monthly Review Cadence</p>

        <div className="space-y-6" data-id="element-115">
          {[{
        time: '30-Day',
        title: 'Check-In Call',
        desc: ['Review early campaign data & lead quality', 'Adjust targeting and ad spend as needed'],
        color: 'border-[#2EAD6D]'
      }, {
        time: '60-Day',
        title: 'Mid-Point Review',
        desc: ['Analyze conversion rates and cost-per-lead', 'Optimize underperforming channels'],
        color: 'border-[#F5A623]'
      }, {
        time: '90-Day',
        title: 'Full Performance Audit',
        desc: ['Compare results to original KPIs', 'Present ROI report and next-quarter roadmap'],
        color: 'border-blue-500'
      }].map((item, idx) => <div key={idx} className={`bg-[#1A1A1A] p-8 rounded-2xl border-l-4 ${item.color} flex flex-col md:flex-row gap-6 items-start md:items-center`} data-id="element-116">
              <div className="md:w-1/3" data-id="element-117">
                <div className="text-3xl font-bold text-white" data-id="element-118">{item.time}</div>
                <div className="text-gray-400 font-medium" data-id="element-119">{item.title}</div>
              </div>
              <div className="md:w-2/3 space-y-2" data-id="element-120">
                {item.desc.map((d, i) => <div key={i} className="flex items-center gap-3 text-gray-200" data-id="element-121">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-122"></div>
                    {d}
                  </div>)}
              </div>
            </div>)}
        </div>
      </div>,
  // SLIDE 9: WHAT WE REVIEW
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6" data-id="element-123">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center" data-id="element-124">
          What We Review
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-125">
          {[{
        icon: BarChart3,
        text: 'Ad Campaign Performance',
        sub: '(clicks, leads, cost-per-lead)'
      }, {
        icon: Monitor,
        text: 'Website Traffic & Conversion Metrics',
        sub: ''
      }, {
        icon: Headset,
        text: 'Call Tracking & Lead Source Breakdown',
        sub: ''
      }, {
        icon: TrendingUp,
        text: 'Budget Utilization & ROI',
        sub: ''
      }, {
        icon: Palette,
        text: 'Creative Performance',
        sub: '(top-performing ads)'
      }, {
        icon: Target,
        text: 'Goal Progress vs. Agreed KPIs',
        sub: ''
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/5 flex items-center gap-6 group hover:bg-white/5 transition-colors" data-id="element-126">
              <div className="bg-[#2EAD6D]/10 p-4 rounded-xl group-hover:bg-[#2EAD6D]/20 transition-colors" data-id="element-127">
                <item.icon className="w-8 h-8 text-[#2EAD6D]" data-id="element-128" />
              </div>
              <div data-id="element-129">
                <h3 className="text-xl font-bold text-white" data-id="element-130">{item.text}</h3>
                {item.sub && <p className="text-gray-400 mt-1" data-id="element-131">{item.sub}</p>}
              </div>
            </div>)}
        </div>
      </div>,
  // SLIDE 10: SUCCESS REQUIREMENTS
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-132">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-133">
          What Drives Success
        </h2>
        <p className="text-xl text-[#F5A623] mb-10" data-id="element-134">
          Success Requirements (Client Responsibilities)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-135">
          {[{
        title: 'Account Access',
        desc: 'Provide ad account, domain, and asset access immediately.'
      }, {
        title: 'Funding',
        desc: 'Ad spend budget must be funded directly on platforms.'
      }, {
        title: 'Feedback Loop',
        desc: 'Approve creatives and strategy within 48 hours to avoid delays.'
      }, {
        title: 'Lead Management',
        desc: 'Partner is responsible for sales, consultations, and onboarding.'
      }, {
        title: 'Compliance',
        desc: 'Ensure all offers and claims meet medical & legal regulations.'
      }, {
        title: 'Performance Data',
        desc: 'Shared feedback on lead quality is vital for optimization.'
      }].map((item, idx) => <div key={idx} className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col h-full" data-id="element-136">
              <div className="flex items-center gap-3 mb-4" data-id="element-137">
                <CheckCircle2 className="w-6 h-6 text-[#2EAD6D]" data-id="element-138" />
                <h3 className="text-xl font-bold text-white" data-id="element-139">{item.title}</h3>
              </div>
              <p className="text-gray-400 leading-relaxed flex-grow" data-id="element-140">
                {item.desc}
              </p>
            </div>)}
        </div>
      </div>,
  // SLIDE 11: WEBSITES
  () => <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-6" data-id="element-141">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-142">
          Website Hosting & Templates
        </h2>

        <div className="bg-[#1A1A1A] p-6 rounded-2xl border border-white/10 mb-8" data-id="element-143">
          <p className="text-gray-200 text-lg mb-2" data-id="element-144">
            Our Web hosting Provider is GoDaddy. We will Host your domain,
            Create your website, and develop your brand identity with the tools
            provided.
          </p>
          <p className="text-[#F5A623] text-sm" data-id="element-145">
            This service only provides securing your .com (excluding .net .co
            .io etc.)
          </p>
        </div>

        <h3 className="text-2xl font-bold text-white mb-6" data-id="element-146">
          Interactive Website Template Demo
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-147">
          {[{
        title: 'Performance Focus',
        url: 'https://levate1.viridiaanalytics.com'
      }, {
        title: 'Clinical / Evidence Focus',
        url: 'https://levate2.viridiaanalytics.com'
      }, {
        title: 'Lifestyle / Transformation Focus',
        url: 'https://levate3.viridiaanalytics.com'
      }].map((template, idx) => <div key={idx} className="group relative rounded-xl overflow-hidden border border-white/10 bg-black hover:border-[#2EAD6D] transition-all" data-id="element-148">
              <div className="aspect-[4/3] relative overflow-hidden bg-gray-900" data-id="element-149">
                <iframe src={template.url} title={template.title} className="w-[200%] h-[200%] origin-top-left scale-50 pointer-events-none border-0" loading="lazy" sandbox="allow-scripts allow-same-origin" data-id="element-150" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent z-10" data-id="element-151"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 z-20" data-id="element-152">
                <span className="font-bold text-white text-sm block mb-2" data-id="element-153">
                  {template.title}
                </span>
                <a href={template.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#2EAD6D] hover:text-[#34C47C] font-medium transition-colors" data-id="element-154">
                  Open Full Preview <ExternalLink className="w-3 h-3" data-id="element-155" />
                </a>
              </div>
            </div>)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs text-gray-500 bg-black/40 p-4 rounded-xl" data-id="element-156">
          <p data-id="element-157">
            * All sites will include "Powered by: Viridia Analytics" in footer
          </p>
          <p data-id="element-158">* Your Brand Color Palette, Logo, and Stock photos will be used</p>
          <p data-id="element-159">* Additional Pages, widgets, funnels subject to additional fees</p>
          <p data-id="element-160">
            * Existing domains require access for tracking (Meta Pixel, UTM)
          </p>
        </div>
      </div>,
  // SLIDE 12: BRANDING
  () => <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-6 text-center" data-id="element-161">
        <h2 className="text-5xl md:text-6xl font-bold text-white mb-4" data-id="element-162">
          Build a trusted <span className="text-[#2EAD6D]" data-id="element-163">BRAND</span>
        </h2>
        <p className="text-gray-400 mb-12 text-lg" data-id="element-164">
          Here's an example of what we'll create for you
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-165">
          {/* Example Logo */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center" data-id="element-166">
            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#1B4D6E] flex items-center justify-center mb-6 shadow-lg" data-id="element-167">
              <div className="text-center" data-id="element-168">
                <Shield className="w-10 h-10 text-[#2EAD6D] mx-auto mb-1" data-id="element-169" />
                <span className="text-white font-bold text-xs tracking-widest" data-id="element-170">
                  APEX
                </span>
              </div>
            </div>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-171">Logo</h4>
            <p className="text-sm text-gray-500" data-id="element-172">Custom mark + wordmark</p>
          </div>

          {/* Example Color Palette */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center" data-id="element-173">
            <div className="flex gap-2 mb-6" data-id="element-174">
              {[{
            color: '#0A2540',
            label: 'Navy'
          }, {
            color: '#2EAD6D',
            label: 'Teal'
          }, {
            color: '#1B4D6E',
            label: 'Steel'
          }, {
            color: '#F5F5F0',
            label: 'Cream'
          }, {
            color: '#D4A853',
            label: 'Gold'
          }].map((swatch, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-175">
                  <div className="w-12 h-12 rounded-lg shadow-md border border-white/10" style={{
              backgroundColor: swatch.color
            }} data-id="element-176" />
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-177">
                    {swatch.label}
                  </span>
                </div>)}
            </div>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-178">Color Palette</h4>
            <p className="text-sm text-gray-500" data-id="element-179">Curated brand colors</p>
          </div>

          {/* Example Brand Motto */}
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5 flex flex-col items-center justify-center" data-id="element-180">
            <MessageSquareQuote className="w-8 h-8 text-[#F5A623] mb-4" data-id="element-181" />
            <p className="text-2xl font-bold text-white italic leading-snug mb-4" data-id="element-182">
              "Strength Restored.
              <br data-id="element-183" />
              Confidence Renewed."
            </p>
            <h4 className="text-lg font-bold text-white mb-1" data-id="element-184">Brand Motto</h4>
            <p className="text-sm text-gray-500" data-id="element-185">Memorable tagline</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#2EAD6D]/10 via-transparent to-[#F5A623]/10 p-10 rounded-3xl border border-white/5" data-id="element-186">
          <h3 className="text-3xl font-bold text-white mb-6" data-id="element-187">
            Build a brand patients remember, not just a logo.
          </h3>
          <p className="text-xl text-gray-400" data-id="element-188">
            Already have branding? Perfect.
            <br data-id="element-189" />
            We'll plug it into high-performing ad creatives that actually get
            clicks.
          </p>
        </div>
      </div>,
  // SLIDE 13: LEGAL
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-190">
        <h2 className="text-3xl font-bold text-gray-300 mb-8 border-b border-white/10 pb-4" data-id="element-191">
          Legal Disclaimer & Important Notices
        </h2>

        <div className="space-y-6 text-sm md:text-base text-gray-500 leading-relaxed" data-id="element-192">
          <p data-id="element-193">
            This presentation is for informational purposes only and does not
            constitute a legal, financial, or earnings guarantee of any kind.
          </p>
          <p data-id="element-194">
            Any revenue, profit, or growth examples are illustrative only;
            actual results vary based on each licensee's skills, effort, market
            conditions, and business decisions.
          </p>
          <p data-id="element-195">
            Past performance of Viridia Analytics, its partners, or other
            licensees does not guarantee future results for you or your
            business.
          </p>
          <p data-id="element-196">
            By participating in this program, you acknowledge that you are
            operating as an independent contractor and not as an employee,
            partner, or agent of Viridia Analytics.
          </p>
          <p data-id="element-197">
            You are solely responsible for complying with all applicable laws,
            regulations, advertising guidelines, and tax obligations in your
            jurisdiction.
          </p>
          <p data-id="element-198">
            Nothing in this presentation should be considered legal, tax, or
            professional advice; consult your own qualified advisors before
            making decisions.
          </p>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-xs text-gray-600" data-id="element-199">
          © 2026 Viridia Analytics. All rights reserved. Confidential and
          proprietary – do not distribute without written permission.
        </div>
      </div>,
  // SLIDE 14: CONTACT INFO
  () => <div className="flex flex-col justify-center h-full max-w-4xl mx-auto px-6" data-id="element-200">
        <div className="text-center mb-12" data-id="element-201">
          <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-24 md:h-32 object-contain mx-auto mb-8" data-id="element-202" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-203">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-400" data-id="element-204">
            We're here to support your success
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-205">
          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-206">
            <div className="flex items-center gap-4 mb-4" data-id="element-207">
              <div className="bg-[#2EAD6D]/10 p-3 rounded-xl" data-id="element-208">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#2EAD6D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-209">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-210" />
                </svg>
              </div>
              <div data-id="element-211">
                <h3 className="text-lg font-bold text-white" data-id="element-212">Phone</h3>
                <p className="text-[#2EAD6D] text-lg font-medium" data-id="element-213">
                  (623) 300-0708
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-214">
            <div className="flex items-center gap-4 mb-4" data-id="element-215">
              <div className="bg-[#F5A623]/10 p-3 rounded-xl" data-id="element-216">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#F5A623]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-217">
                  <circle cx="12" cy="12" r="10" data-id="element-218" />
                  <polyline points="12 6 12 12 16 14" data-id="element-219" />
                </svg>
              </div>
              <div data-id="element-220">
                <h3 className="text-lg font-bold text-white" data-id="element-221">Business Hours</h3>
                <p className="text-gray-300" data-id="element-222">Mon – Friday</p>
                <p className="text-[#F5A623] font-medium" data-id="element-223">
                  9:00 AM – 4:00 PM (AZ Time)
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-224">
            <div className="flex items-center gap-4 mb-4" data-id="element-225">
              <div className="bg-[#2EAD6D]/10 p-3 rounded-xl" data-id="element-226">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#2EAD6D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-227">
                  <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-228" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-229" />
                </svg>
              </div>
              <div data-id="element-230">
                <h3 className="text-lg font-bold text-white" data-id="element-231">
                  General Inquiries
                </h3>
                <a href="mailto:info@viridiaanalytics.com" className="text-[#2EAD6D] hover:text-[#34C47C] font-medium transition-colors" data-id="element-232">
                  info@viridiaanalytics.com
                </a>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-2xl border border-white/5" data-id="element-233">
            <div className="flex items-center gap-4 mb-4" data-id="element-234">
              <div className="bg-[#F5A623]/10 p-3 rounded-xl" data-id="element-235">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#F5A623]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-236">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4-4v2" data-id="element-237" />
                  <circle cx="9" cy="7" r="4" data-id="element-238" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" data-id="element-239" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-240" />
                </svg>
              </div>
              <div data-id="element-241">
                <h3 className="text-lg font-bold text-white" data-id="element-242">
                  Point of Contact (Support)
                </h3>
                <a href="mailto:kevin@viridiaanalytics.com" className="text-[#F5A623] hover:text-[#F7B84E] font-medium transition-colors" data-id="element-243">
                  kevin@viridiaanalytics.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>,
  // SLIDE 15: CLOSING
  () => <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-244">
        <div className="flex items-center justify-center gap-6 mb-12" data-id="element-245">
          <img src={LOGO_LEVATE} alt="L-Evate" className="h-12 md:h-16 object-contain opacity-80" data-id="element-246" />
          <span className="text-2xl font-light text-gray-600" data-id="element-247">x</span>
          <img src={LOGO_VIRIDIA} alt="Viridia Analytics" className="h-28 md:h-36 object-contain" data-id="element-248" />
        </div>

        <h2 className="text-5xl md:text-7xl font-bold text-white mb-6" data-id="element-249">
          Ready to Elevate Your Business?
        </h2>
        <p className="text-2xl text-[#2EAD6D] font-medium mb-16" data-id="element-250">
          Let's build something powerful together.
        </p>

        <button className="bg-gradient-to-r from-[#2EAD6D] to-[#248A55] hover:from-[#34C47C] hover:to-[#2EAD6D] text-white px-10 py-5 rounded-full text-xl font-bold transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(46,173,109,0.3)]" data-id="element-251">
          Get Started Today
        </button>
      </div>];
  return <div className="min-h-screen bg-[#0F0F0F] text-white overflow-hidden flex flex-col font-sans selection:bg-[#2EAD6D] selection:text-white" data-id="element-252">
      {/* Progress Bar */}
      <div className="h-1.5 w-full bg-white/5 fixed top-0 left-0 z-50" data-id="element-253">
        <div className="h-full bg-gradient-to-r from-[#F5A623] to-[#2EAD6D] transition-all duration-500 ease-out" style={{
        width: `${(currentSlide + 1) / totalSlides * 100}%`
      }} data-id="element-254" />
      </div>

      {/* Main Slide Content */}
      <main className="flex-grow relative" data-id="element-255">
        <div key={currentSlide} className="absolute inset-0 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both" data-id="element-256">
          {slides[currentSlide]()}
        </div>
      </main>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-6 bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 z-50" data-id="element-257">
        <button onClick={prevSlide} disabled={currentSlide === 0} className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors" aria-label="Previous slide" data-id="element-258">
          <ChevronLeft className="w-6 h-6" data-id="element-259" />
        </button>

        <div className="text-sm font-medium text-gray-300 min-w-[4rem] text-center" data-id="element-260">
          {currentSlide + 1} / {totalSlides}
        </div>

        <button onClick={nextSlide} disabled={currentSlide === totalSlides - 1} className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors" aria-label="Next slide" data-id="element-261">
          <ChevronRight className="w-6 h-6" data-id="element-262" />
        </button>
      </div>
    </div>;
}